import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Constants } from "@/constants";

const COGNITO_LOGIN_URL = `${Constants.REACT_APP_API_BASE_URL}auth/cognito/login`;

export default function Login() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "MarketBuzz";
  }, []);

  const handleCognitoLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      if (!username.trim() || !password) {
        setError("Username Or Email and password are required.");
        return;
      }
      setLoading(true);
      try {
        // Get source and state_key from query params if present (for ChatGPT OAuth flow)
        const source = searchParams.get("source");
        const stateKey = searchParams.get("state_key");

        const res = await fetch(COGNITO_LOGIN_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: username.trim(),
            password,
            ...(source && { source }),
            ...(stateKey && { state_key: stateKey })
          })
        });
        const data = (await res.json().catch(() => ({}))) as {
          redirectUrl?: string;
          message?: string;
        };
        if (res.ok && data.redirectUrl) {
          window.location.href = data.redirectUrl;
          return;
        }
        if (res.status === 401) {
          setError("Invalid credentials.");
          return;
        }
        if (res.status === 403) {
          setError(
            "No access configured; use Sign in with Clover or contact your admin."
          );
          return;
        }
        if (
          data.message &&
          (data.message.includes("cognito-idp:AdminInitiateAuth") ||
            data.message.includes("not authorized to perform"))
        ) {
          setError("Invalid username or password.");
          return;
        }

        setError(data.message || "Sign in failed. Please try again.");
      } catch {
        setError("Sign in failed. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [username, password, searchParams]
  );

  const handleLoginClover = useCallback(() => {
    const clientId = searchParams.get("client_id");
    const source = searchParams.get("source");
    const stateKey = searchParams.get("state_key");

    // When source=chatgpt, the client_id is the ChatGPT OAuth client ID, NOT a Clover app client ID.
    // In this case, redirect to Clover without client_id (will go to app selection) but preserve source and state_key.
    const isChatGPTFlow = source === "chatgpt";

    if (clientId && !isChatGPTFlow) {
      // Normal Clover flow with a valid Clover client_id
      const baseURL = `${Constants.CLOVER_SERVICE_BASE_URL}auth/clover/login`;
      const urlParams = new URLSearchParams();
      urlParams.set("client_id", clientId);
      for (const [key, value] of searchParams.entries()) {
        if (key !== "client_id") {
          urlParams.set(key, value);
        }
      }
      window.location.href = `${baseURL}?${urlParams.toString()}`;
    } else if (isChatGPTFlow) {
      // ChatGPT flow: redirect to Clover login without client_id (will redirect to app selection)
      // Preserve source, state_key, and redirectURI (ChatGPT app callback) so Clover can redirect to ChatGPT after OAuth.
      const chatgptCallbackUrl = searchParams.get("chatgpt_callback_url");
      const baseURL = `${Constants.CLOVER_SERVICE_BASE_URL}auth/clover/login`;
      const urlParams = new URLSearchParams();
      if (source) urlParams.set("source", source);
      if (stateKey) urlParams.set("state_key", stateKey);
      if (chatgptCallbackUrl) urlParams.set("redirectURI", chatgptCallbackUrl);
      window.location.href = `${baseURL}?${urlParams.toString()}`;
    } else {
      // No client_id and not ChatGPT flow: go to local app selection
      navigate("/auth/clover/app-selection");
    }
  }, [searchParams, navigate]);

  const rightPanelBg = "#F1F3F4";

  return (
    <div className="flex min-h-screen">
      {/* Left: illustration panel (~45% width), white area */}
      <div
        className="hidden lg:block w-[45%] min-h-screen relative overflow-hidden shrink-0"
        style={{ backgroundColor: "#ffffff" }}
      >
        <img
          src="/loginImage.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>

      {/* Vertical divider between left and right */}
      <div
        className="hidden lg:block w-px shrink-0 min-h-screen"
        style={{ backgroundColor: "#E5E7EB" }}
        aria-hidden
      />

      {/* Right: login form (~55% width), light gray area */}
      <div
        className="flex-1 flex items-center justify-center min-h-screen p-6 lg:p-12 min-w-0"
        style={{ backgroundColor: rightPanelBg }}
      >
        <div className="w-full max-w-sm">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-[#1a1a1a]">
              Welcome Back !
            </h1>
            <p className="text-gray-500 text-sm">Sign in to continue.</p>
          </div>

          <form onSubmit={handleCognitoLogin} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-[#1a1a1a]">
                Username Or Email
              </Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                value={username ?? ""}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter Username Or Email"
                className="w-full rounded-xl bg-[#e8f4fc] border-[#e8f4fc] text-[#1a1a1a] placeholder:text-gray-400 focus-visible:ring-2 focus-visible:ring-[#128f8b] focus-visible:ring-offset-0"
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#1a1a1a]">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password ?? ""}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder=""
                  className="w-full rounded-xl bg-[#e8f4fc] border-[#e8f4fc] text-[#1a1a1a] placeholder:text-gray-400 focus-visible:ring-2 focus-visible:ring-[#128f8b] focus-visible:ring-offset-0 pr-10"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#128f8b] focus:ring-offset-0 rounded p-1"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              variant="default"
              className="w-full rounded-xl bg-[#128f8b] hover:bg-[#0f7a76] text-white h-11 font-semibold"
              disabled={loading}
            >
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>

          <div className="mt-4">
            <Button
              type="button"
              variant="default"
              className="w-full rounded-xl bg-[#00b140] hover:bg-[#009433] text-white h-11 font-semibold"
              onClick={handleLoginClover}
            >
              <span className="flex items-center justify-center gap-2">
                <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white">
                  <img src="/clover-logo.svg" alt="" className="h-4 w-4" />
                </span>
                <span>Sign In with Clover</span>
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
